import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ChevronLeft, ChevronRight, Heart, Users, TreePine, Home as HomeIcon, Star, Send, Clock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const heroSlides = [
  { image: "https://images.unsplash.com/photo-1483671174579-bab2a5293389?w=1920&q=80", title: "Ethiopie", subtitle: "Au berceau de l'humanite", slug: "ethiopie" },
  { image: "https://images.unsplash.com/photo-1544704325-8c4f82787278?w=1920&q=80", title: "Djibouti", subtitle: "Terre de sel et de feu", slug: "djibouti" },
  { image: "https://images.pexels.com/photos/2643896/pexels-photo-2643896.jpeg?auto=compress&w=1920", title: "Myanmar", subtitle: "Pagodes dorees et sourires eternels", slug: "myanmar" },
  { image: "https://images.pexels.com/photos/27629365/pexels-photo-27629365.jpeg?auto=compress&w=1920", title: "Vietnam Sapa", subtitle: "Rizieres et minorites ethniques", slug: "vietnam-sapa" },
  { image: "https://images.unsplash.com/photo-1611015078598-ad59ad26e203?w=1920&q=80", title: "Perou", subtitle: "Des Andes a l'Amazonie", slug: "perou" },
];

const philosophyItems = [
  { icon: Heart, title: "Rencontres authentiques", text: "Chaque voyage est concu autour de la rencontre des peuples. Trois nuits minimum chez l'habitant dans chaque circuit." },
  { icon: HomeIcon, title: "Home-staying", text: "Dormir chez l'habitant, partager les repas, vivre au rythme des communautes locales. Le vrai voyage commence la." },
  { icon: TreePine, title: "Tourisme responsable", text: "Nous nous engageons pour un tourisme respectueux des cultures et de l'environnement. Groupes limites a 12 personnes." },
  { icon: Users, title: "Tourisme solidaire", text: "L'argent de vos sejours chez l'habitant beneficie directement aux communautes. Nous soutenons des projets locaux." },
];

const galleryImages = [
  "https://images.unsplash.com/photo-1763479168168-657afe79882c?w=600&q=80",
  "https://images.pexels.com/photos/6752824/pexels-photo-6752824.jpeg?auto=compress&w=600",
  "https://images.pexels.com/photos/235925/pexels-photo-235925.jpeg?auto=compress&w=600",
  "https://images.unsplash.com/photo-1594708764147-9739e5dcd385?w=600&q=80",
  "https://images.unsplash.com/photo-1525383033081-6088a7a5affa?w=600&q=80",
  "https://images.unsplash.com/photo-1573403092240-26095e118918?w=600&q=80",
  "https://images.pexels.com/photos/453316/pexels-photo-453316.jpeg?auto=compress&w=600",
  "https://images.unsplash.com/photo-1609922565804-e563a9968796?w=600&q=80",
];

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [destinations, setDestinations] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [destRes, testRes] = await Promise.all([
          axios.get(`${API}/destinations`),
          axios.get(`${API}/testimonials`),
        ]);
        setDestinations(destRes.data.filter(d => d.is_featured));
        setTestimonials(testRes.data);
      } catch (e) {
        console.error("Fetch error:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % heroSlides.length);
  }, []);

  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Inscription reussie ! Merci de votre interet.");
      setEmail("");
    } catch {
      toast.error("Erreur lors de l'inscription");
    }
  };

  return (
    <div>
      {/* HERO SECTION */}
      <section className="relative h-[90vh] overflow-hidden" data-testid="hero-section">
        {heroSlides.map((slide, i) => (
          <div
            key={i}
            className={`hero-slide ${i === currentSlide ? 'active' : 'inactive'}`}
          >
            <img
              src={slide.image}
              alt={slide.title}
              className="w-full h-full object-cover"
              loading={i === 0 ? "eager" : "lazy"}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2D5016]/80 via-[#2D5016]/20 to-transparent" />
          </div>
        ))}

        <div className="absolute inset-0 z-10 flex flex-col justify-end pb-20 px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <h2 className="text-[#C8A951] font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-wide mb-3 animate-fade-in drop-shadow-lg">
              {heroSlides[currentSlide].title}
            </h2>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-3">
              Voyagez autrement.<br />Rencontrez le monde.
            </h1>
            <p className="text-white/90 font-heading text-lg md:text-xl mb-8 max-w-xl font-medium italic">
              {heroSlides[currentSlide].subtitle}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to={`/destinations/${heroSlides[currentSlide].slug}`}
                className="bg-[#C8A951] text-[#2C2C2C] rounded-full px-8 py-3 font-body font-medium hover:bg-[#C8A951]/90 transition-all shadow-lg"
                data-testid="hero-cta-discover"
              >
                Decouvrir ce voyage
              </Link>
              <Link
                to="/demande-de-devis"
                className="border-2 border-white text-white rounded-full px-8 py-3 font-body font-medium hover:bg-white hover:text-[#2D5016] transition-all"
                data-testid="hero-cta-devis"
              >
                Demander un devis
              </Link>
            </div>
          </div>
        </div>

        {/* Slide controls */}
        <div className="absolute bottom-6 right-6 md:right-12 z-10 flex items-center gap-3">
          <button onClick={prevSlide} className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/40 transition-colors" data-testid="hero-prev">
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
          <div className="flex gap-2">
            {heroSlides.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentSlide(i)}
                className={`w-2 h-2 rounded-full transition-all ${i === currentSlide ? 'bg-[#C8A951] w-6' : 'bg-white/50'}`}
                data-testid={`hero-dot-${i}`}
              />
            ))}
          </div>
          <button onClick={nextSlide} className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/40 transition-colors" data-testid="hero-next">
            <ChevronRight className="w-5 h-5 text-white" />
          </button>
        </div>
      </section>

      {/* COUPS DE COEUR */}
      <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24" data-testid="coups-de-coeur-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#C8A951] font-body text-sm tracking-[0.3em] uppercase mb-3">Nos coups de coeur</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-[#2C2C2C]">
              Destinations d'exception
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {(destinations.length ? destinations : []).map((dest, i) => (
              <Link
                key={dest.slug || i}
                to={`/destinations/${dest.slug}`}
                className={`destination-card group relative overflow-hidden rounded-xl ${i === 0 ? 'sm:col-span-2 lg:col-span-2 aspect-[16/9]' : 'aspect-[3/4]'}`}
                data-testid={`destination-card-${dest.slug}`}
              >
                <img
                  src={dest.card_image}
                  alt={dest.name}
                  className="card-image w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2C2C2C]/80 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="text-[#C8A951] font-body text-xs tracking-widest uppercase mb-1">{dest.duration} jours</p>
                  <h3 className="font-heading text-xl md:text-2xl font-semibold text-white mb-1">{dest.name}</h3>
                  <p className="text-white/80 font-body text-sm italic mb-3">{dest.tagline}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-[#C8A951] font-body font-bold text-lg">A partir de {dest.price} EUR</span>
                    <span className="text-white font-body text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                      En savoir plus <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PHILOSOPHIE */}
      <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-[#2D5016]" data-testid="philosophy-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#C8A951] font-body text-sm tracking-[0.3em] uppercase mb-3">Notre philosophie</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-[#FDF8F0]">
              Le voyage qui transforme
            </h2>
            <p className="text-[#FDF8F0]/80 font-body text-base md:text-lg mt-4 max-w-2xl mx-auto">
              Chez VisitMonde, nous croyons que le vrai voyage est celui qui cree des liens entre les peuples. Nos circuits sont concus pour vous immerger dans la vie quotidienne des communautes locales.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {philosophyItems.map((item, i) => (
              <div key={i} className="text-center p-6 rounded-xl bg-[#FDF8F0]/10 backdrop-blur-sm hover:bg-[#FDF8F0]/15 transition-colors" data-testid={`philosophy-item-${i}`}>
                <div className="w-14 h-14 rounded-full bg-[#C8A951]/20 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-7 h-7 text-[#C8A951]" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-[#FDF8F0] mb-2">{item.title}</h3>
                <p className="font-body text-sm text-[#FDF8F0]/70 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PHOTO GALLERY */}
      <section className="py-20 md:py-32 relative gallery-container overflow-hidden" data-testid="gallery-section">
        <div className="text-center mb-12 px-6">
          <p className="text-[#C8A951] font-body text-sm tracking-[0.3em] uppercase mb-3">Nos voyages en images</p>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#2C2C2C]">Galerie d'inspirations</h2>
        </div>
        <div className="flex gap-4 overflow-x-auto gallery-scroll px-6 pb-4">
          {galleryImages.map((img, i) => (
            <div key={i} className="shrink-0 w-72 h-48 md:w-96 md:h-64 rounded-xl overflow-hidden">
              <img src={img} alt={`Galerie voyage ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy" />
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-[#EFEBE5]" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#C8A951] font-body text-sm tracking-[0.3em] uppercase mb-3">Temoignages</p>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#2C2C2C]">Ils ont voyage avec nous</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-[0_4px_20px_-2px_rgba(45,80,22,0.08)]" data-testid={`testimonial-${i}`}>
                <div className="flex gap-1 mb-4">
                  {[...Array(t.rating)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-[#C8A951] text-[#C8A951]" />
                  ))}
                </div>
                <p className="font-body text-sm text-[#2C2C2C]/80 leading-relaxed mb-4 italic">"{t.text}"</p>
                <div className="border-t border-[#DBCFB0]/50 pt-3">
                  <p className="font-body text-sm font-bold text-[#2C2C2C]">{t.name}</p>
                  <p className="font-body text-xs text-[#A0522D]">{t.destination}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24" data-testid="newsletter-section">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-[#C8A951] font-body text-sm tracking-[0.3em] uppercase mb-3">Newsletter</p>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#2C2C2C] mb-4">
            Recevez nos inspirations voyage
          </h2>
          <p className="font-body text-[#6B6B6B] mb-8">
            Inscrivez-vous pour recevoir nos meilleures offres, recits de voyage et conseils d'experts.
          </p>
          <form onSubmit={handleNewsletter} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <Input
              type="email"
              placeholder="Votre adresse email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 rounded-full px-6 py-3 border-[#DBCFB0] bg-white font-body"
              data-testid="newsletter-email-input"
              required
            />
            <Button
              type="submit"
              className="bg-[#2D5016] text-[#FDF8F0] rounded-full px-8 py-3 hover:bg-[#2D5016]/90 font-body"
              data-testid="newsletter-submit-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              S'inscrire
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
